<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import snakecaseKeys from 'snakecase-keys';
import Spinner from 'shared/components/Spinner.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import AccordionItem from 'dashboard/components/Accordion/AccordionItem.vue';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

// QuePasa Service
const quepasaService = window.quepasa?.service;

const store = useStore();
const currentUser = computed(() => store.getters.getCurrentUser);
const isSuperAdmin = computed(() => currentUser.value?.type === 'SuperAdmin');
const isAccountAdmin = computed(
  () => currentUser.value?.role === 'administrator'
);
const canFixWebhooks = computed(
  () => isAccountAdmin.value || isSuperAdmin.value
);

// Webhooks state
const webhooks = ref([]);
const loadingWebhooks = ref(false);
const serverInfo = ref(null);
const serverInfoError = ref(null);
const expandedWebhooks = ref([]);

// Webhook validation state
const webhookValidation = ref(null);
const loadingValidation = ref(false);
const syncingToWebhook = ref(false);
const syncingFromWebhook = ref(false);

// User validation state
const userValidation = ref(null);
const validatingUser = ref(false);

// QuePasa configuration check
const quepasaConfig = ref(null);
const loadingConfig = ref(false);

// Editing state for each webhook
const editingWebhooks = ref([]);
const savingWebhook = ref({});

// Custom outbound webhook parameters (stored in additional_attributes.outbound_params)
const outboundParams = ref([{ key: '', value: '' }]);
const savingOutboundParams = ref(false);
const outboundParamsDirty = computed(() => {
  const stored =
    props.inbox.channel?.additionalAttributes?.outboundParams || {};
  const current = {};
  outboundParams.value.forEach(p => {
    if (p.key && p.key.trim()) current[p.key.trim()] = p.value;
  });
  return JSON.stringify(stored) !== JSON.stringify(current);
});

// New webhook state
const showNewWebhookModal = ref(false);
const newWebhook = ref({
  url: '',
  trackid: '',
  forwardinternal: true,
  groups: null,
  broadcasts: null,
  calls: null,
  readreceipts: null,
  extras: [{ key: '', value: '' }],
});
const creatingWebhook = ref(false);

// Check QuePasa configuration (for super admins)
const checkQuepasaConfig = async () => {
  if (!isSuperAdmin.value) return;

  try {
    loadingConfig.value = true;
    const accountId = window.location.pathname.split('/')[3];

    const result = await quepasaService.server.config(accountId);

    quepasaConfig.value = result;
  } catch (error) {
    console.error('Error checking QuePasa config:', error);
  } finally {
    loadingConfig.value = false;
  }
};

// Validate QuePasa user first
const validateQuepasaUser = async () => {
  try {
    validatingUser.value = true;
    const accountId = window.location.pathname.split('/')[3];

    const result = await quepasaService.validation.user(
      accountId,
      props.inbox.id
    );

    userValidation.value = result;
    return result.valid;
  } catch (error) {
    userValidation.value = {
      valid: false,
      error: error.message,
    };
    return false;
  } finally {
    validatingUser.value = false;
  }
};

// Validate webhook configuration (only if user is valid)
const validateWebhookConfig = async () => {
  try {
    loadingValidation.value = true;

    // First, validate user
    const userValid = await validateQuepasaUser();

    if (!userValid) {
      webhookValidation.value = {
        to_chatwoot: {
          configured: false,
          valid: false,
          error_type: 'user_not_configured',
          message:
            'Cannot validate webhooks: QuePasa user not configured or missing permissions',
        },
        from_chatwoot: {
          configured: false,
          valid: false,
          error_type: 'user_not_configured',
          message:
            'Cannot validate webhooks: QuePasa user not configured or missing permissions',
        },
      };
      return;
    }

    const accountId = window.location.pathname.split('/')[3];
    const result = await quepasaService.server.info(accountId, props.inbox.id);

    webhookValidation.value = result.webhook_status;
  } catch (error) {
    useAlert(
      `Error validating webhooks: ${error.response?.data?.error || error.message}`
    );
  } finally {
    loadingValidation.value = false;
  }
};

// INBOUND WEBHOOK SYNC: Messages FROM QuePasa TO Chatwoot (QuePasa → N8N → Chatwoot)
const syncInboundWebhook = async () => {
  try {
    syncingToWebhook.value = true;

    const accountId = window.location.pathname.split('/')[3];
    const result = await quepasaService.webhook.configure(
      accountId,
      props.inbox.id,
      'to'
    );

    if (result.to?.configured && result.to?.valid) {
      useAlert('Webhook synced successfully!');
      await validateWebhookConfig();
      await loadWebhooks();
    } else if (result.to?.error_type === 'server_not_found') {
      // Server doesn't exist in QuePasa - show specific error
      const confirmed = confirm(
        'Server not found in QuePasa. Would you like to initialize it now?'
      );
      if (confirmed) {
        await initializeServer();
      }
    } else if (result.to?.error_type === 'user_not_configured') {
      // User not configured - show specific error
      useAlert(`Cannot fix webhook: ${result.to.message}`);
      await validateWebhookConfig(); // Refresh validation to show user error
    }
  } catch (error) {
    // Check if error response contains server_not_found or user_not_configured
    const errorType = error.response?.data?.webhook_status?.error_type;

    if (errorType === 'server_not_found') {
      const confirmed = confirm(
        'Server not found in QuePasa. Would you like to initialize it now?'
      );
      if (confirmed) {
        await initializeServer();
      }
    } else if (errorType === 'user_not_configured') {
      useAlert(
        `Cannot fix webhook: ${error.response?.data?.webhook_status?.message || 'QuePasa user not configured'}`
      );
      await validateWebhookConfig(); // Refresh validation to show user error
    } else {
      useAlert(
        `Error syncing webhook: ${error.response?.data?.error || error.message}`
      );
    }
  } finally {
    syncingToWebhook.value = false;
  }
};

// Initialize server in QuePasa
const initializeServer = async () => {
  try {
    syncingToWebhook.value = true;

    const accountId = window.location.pathname.split('/')[3];
    const result = await quepasaService.server.create(
      accountId,
      props.inbox.id,
      {}
    );

    if (result.success) {
      useAlert('Server initialized successfully! Now syncing webhook...');
      // After initializing, try to sync webhook again
      await syncInboundWebhook();
    }
  } catch (error) {
    useAlert(`Error initializing server: ${error.message}`);
  } finally {
    syncingToWebhook.value = false;
  }
};

// OUTBOUND WEBHOOK SYNC: Messages FROM Chatwoot TO QuePasa (Chatwoot → N8N → QuePasa)
const syncOutboundWebhook = async () => {
  try {
    syncingFromWebhook.value = true;

    const accountId = window.location.pathname.split('/')[3];
    const result = await quepasaService.webhook.configure(
      accountId,
      props.inbox.id,
      'from'
    );

    if (result.from?.configured && result.from?.valid) {
      useAlert('Outgoing webhook updated successfully!');
      await validateWebhookConfig();
    } else if (result.from?.error_type === 'user_not_configured') {
      // User not configured - show specific error
      useAlert(
        `Cannot fix webhook: ${result.from?.message || 'QuePasa user not configured'}`
      );
      await validateWebhookConfig(); // Refresh validation to show user error
    }
  } catch (error) {
    useAlert(`Error updating outgoing webhook: ${error.message}`);
  } finally {
    syncingFromWebhook.value = false;
  }
};

// Get user-friendly error type label
const getErrorTypeLabel = errorType => {
  const labels = {
    not_configured: 'Not configured',
    user_not_configured: 'QuePasa User not found in Account',
    server_not_found: 'Server not found in QuePasa',
    trackid_mismatch: 'Trackid mismatch',
    trackid_conflict: 'URL conflict',
    base_path_mismatch: 'Base URL mismatch',
    forwardinternal_mismatch: 'forwardinternal not true',
    identifier_mismatch: 'Identifier mismatch',
    utoken_mismatch: 'User token mismatch',
    utoken_invalid: 'User token is invalid',
    utoken_no_access: 'User token does not have access to this account',
    utoken_insufficient_role: 'User token does not have administrator role',
    account_mismatch: 'Account ID mismatch',
    inbox_mismatch: 'Inbox ID mismatch',
    qphost_localhost_variant:
      'Host uses different localhost format (localhost vs 127.0.0.1)',
    base_path_localhost_variant:
      'N8N webhook uses different localhost format (localhost vs 127.0.0.1)',
    invalid_url: 'Invalid URL format',
    url_mismatch: 'URL mismatch',
    sync_error: 'Sync error',
  };
  return labels[errorType] || 'Configuration mismatch';
};

// Check if error is a minor warning (doesn't prevent functionality)
const isMinorWarning = errorType => {
  return (
    errorType === 'qphost_localhost_variant' ||
    errorType === 'base_path_localhost_variant'
  );
};

// Load webhooks from server
const loadWebhooks = async () => {
  try {
    loadingWebhooks.value = true;
    serverInfoError.value = null;

    const accountId = window.location.pathname.split('/')[3];
    const result = await quepasaService.server.info(accountId, props.inbox.id);

    // Store server info for visibility checks
    serverInfo.value = result.server;

    webhooks.value = result.server?.dispatching || [];

    // Update webhook validation with response
    if (result.webhook_status) {
      webhookValidation.value = result.webhook_status;
    }

    // Initialize expanded state (all collapsed by default)
    expandedWebhooks.value = webhooks.value.map(() => false);

    // Initialize editing state for each webhook
    editingWebhooks.value = webhooks.value.map(webhook => ({
      trackid: webhook.trackid || '',
      connection_string: webhook.connection_string || '',
      groups: webhook.groups ?? null,
      broadcasts: webhook.broadcasts ?? null,
      calls: webhook.calls ?? null,
      readreceipts: webhook.readreceipts ?? null,
      forwardinternal: webhook.forwardinternal ?? false,
      extras: webhook.extra
        ? Object.entries(webhook.extra).map(([key, value]) => ({
            key,
            value: String(value),
          }))
        : [{ key: '', value: '' }],
    }));
  } catch (error) {
    // If it's a server_not_found error, set the flag but don't show duplicate alert
    if (
      error.response?.status === 404 ||
      error.message.includes('server not found')
    ) {
      serverInfoError.value = 'server_not_found';
      return;
    }

    // Only show error alert for actual connection/server errors
    serverInfoError.value = error.response?.data?.error || error.message;
    useAlert(
      `Error loading webhooks: ${error.response?.data?.error || error.message}`
    );
  } finally {
    loadingWebhooks.value = false;
  }
};

// Add extra parameter to webhook
const addExtra = webhookIndex => {
  editingWebhooks.value[webhookIndex].extras.push({ key: '', value: '' });
};

// Remove extra parameter from webhook
const removeExtra = (webhookIndex, extraIndex) => {
  const extras = editingWebhooks.value[webhookIndex].extras;
  extras.splice(extraIndex, 1);
  if (extras.length === 0) {
    extras.push({ key: '', value: '' });
  }
};

// Save webhook changes
const saveWebhook = async webhookIndex => {
  try {
    savingWebhook.value[webhookIndex] = true;

    const webhook = editingWebhooks.value[webhookIndex];

    // Build request body with all editable fields
    const payload = {
      inbox_id: props.inbox.id,
    };

    // Add boolean options (send even if null - null means "use server config")
    if (webhook.groups !== undefined) payload.groups = webhook.groups;
    if (webhook.broadcasts !== undefined)
      payload.broadcasts = webhook.broadcasts;
    if (webhook.calls !== undefined) payload.calls = webhook.calls;
    if (webhook.readreceipts !== undefined)
      payload.readreceipts = webhook.readreceipts;
    if (webhook.forwardinternal !== undefined)
      payload.forwardinternal = webhook.forwardinternal;

    // Build extras object from editing array
    // Always send extras if the field exists, even if empty (to allow clearing)
    const extras = {};
    if (webhook.extras && webhook.extras.length > 0) {
      webhook.extras.forEach(item => {
        if (item.key && item.key.trim()) {
          extras[item.key.trim()] = item.value;
        }
      });
    }
    // Send extras even if empty to allow clearing
    payload.extras = extras;

    const accountId = window.location.pathname.split('/')[3];
    const result = await quepasaService.server.update(
      accountId,
      props.inbox.id,
      payload
    );

    if (result.success) {
      useAlert('Webhook updated successfully!');
      await loadWebhooks();
    }
  } catch (error) {
    useAlert(`Error saving webhook: ${error.message}`);
  } finally {
    savingWebhook.value[webhookIndex] = false;
  }
};

// Delete webhook
const deleteWebhook = async webhookIndex => {
  // eslint-disable-next-line no-alert, no-restricted-globals
  if (
    !window.confirm(
      'Are you sure you want to delete this webhook? This action cannot be undone.'
    )
  ) {
    return;
  }

  try {
    loadingWebhooks.value = true;
    const accountId = window.location.pathname.split('/')[3];
    const result = await quepasaService.webhook.delete(
      accountId,
      props.inbox.id
    );

    if (result.success) {
      useAlert('Webhook deleted successfully!');
      await loadWebhooks();
    }
  } catch (error) {
    useAlert(`Error deleting webhook: ${error.message}`);
  } finally {
    loadingWebhooks.value = false;
  }
};

// Toggle webhook expansion
const toggleWebhook = index => {
  expandedWebhooks.value = expandedWebhooks.value.map((val, idx) =>
    idx === index ? !val : val
  );
};

// Check if webhook has any boolean options
const hasAnyBooleanOption = webhook => {
  return (
    webhook.groups !== undefined ||
    webhook.broadcasts !== undefined ||
    webhook.calls !== undefined ||
    webhook.readreceipts !== undefined ||
    webhook.forwardinternal !== undefined
  );
};

// Toggle tri-state value (null -> false -> true -> null)
const toggleTriState = (index, field) => {
  const current = editingWebhooks.value[index][field];
  let newValue;
  if (current === null) {
    newValue = false;
  } else if (current === false) {
    newValue = true;
  } else {
    newValue = null;
  }
  // Garantir reatividade criando novo objeto
  editingWebhooks.value[index] = {
    ...editingWebhooks.value[index],
    [field]: newValue,
  };
};

// Get icon and color for tri-state display
const getTriStateDisplay = value => {
  if (value === true) {
    return { icon: 'checkmark', color: 'text-green-600' };
  }
  if (value === false) {
    return { icon: 'dismiss', color: 'text-red-600' };
  }
  return { icon: 'subtract', color: 'text-slate-400' };
};

// Test webhook
const testWebhook = async index => {
  try {
    useAlert('Testing webhook...');
    const accountId = window.location.pathname.split('/')[3];
    const result = await quepasaService.webhook.test(accountId, props.inbox.id);
    if (result.success) {
      useAlert('Webhook test successful!');
    }
  } catch (error) {
    useAlert(`Test failed: ${error.message}`);
  }
};

// Validate webhook
const validateWebhook = async index => {
  try {
    const webhook = webhooks.value[index];
    if (!webhook.connection_string) {
      useAlert('Invalid webhook URL');
      return;
    }
    useAlert('Webhook is valid!');
  } catch (error) {
    useAlert('Webhook validation failed');
  }
};

// Copy webhook URL to clipboard
const copyWebhookUrl = async url => {
  try {
    await navigator.clipboard.writeText(url);
    useAlert('URL copied to clipboard!');
  } catch (error) {
    useAlert('Failed to copy URL');
  }
};

// Add extra field to new webhook
const addNewWebhookExtra = () => {
  newWebhook.value.extras.push({ key: '', value: '' });
};

// Remove extra field from new webhook
const removeNewWebhookExtra = index => {
  newWebhook.value.extras.splice(index, 1);
  if (newWebhook.value.extras.length === 0) {
    newWebhook.value.extras.push({ key: '', value: '' });
  }
};

// Create new webhook
const createWebhook = async () => {
  if (!newWebhook.value.url || !newWebhook.value.trackid) {
    useAlert('URL and Track ID are required');
    return;
  }

  try {
    creatingWebhook.value = true;

    // Build extras object
    const extras = {};
    newWebhook.value.extras.forEach(item => {
      if (item.key && item.key.trim()) {
        extras[item.key.trim()] = item.value;
      }
    });

    // Build payload
    const payload = {
      url: newWebhook.value.url,
      trackid: newWebhook.value.trackid,
      forwardinternal: newWebhook.value.forwardinternal,
    };

    // Add extras if any
    if (Object.keys(extras).length > 0) {
      payload.extra = extras;
    }

    // Add boolean options (only if not null)
    if (newWebhook.value.groups !== null)
      payload.groups = newWebhook.value.groups;
    if (newWebhook.value.broadcasts !== null)
      payload.broadcasts = newWebhook.value.broadcasts;
    if (newWebhook.value.calls !== null) payload.calls = newWebhook.value.calls;
    if (newWebhook.value.readreceipts !== null)
      payload.readreceipts = newWebhook.value.readreceipts;

    const accountId = window.location.pathname.split('/')[3];
    const result = await quepasaService.webhook.create(
      accountId,
      props.inbox.id
    );

    if (result.success) {
      useAlert('Webhook created successfully!');
      showNewWebhookModal.value = false;
      // Reset form
      newWebhook.value = {
        url: '',
        trackid: '',
        forwardinternal: true,
        groups: null,
        broadcasts: null,
        calls: null,
        readreceipts: null,
        extras: [{ key: '', value: '' }],
      };
      await loadWebhooks();
    }
  } catch (error) {
    useAlert(
      `Error creating webhook: ${error.response?.data?.error || error.message}`
    );
  } finally {
    creatingWebhook.value = false;
  }
};

// Add custom outbound parameter row
const addOutboundParam = () => {
  outboundParams.value.push({ key: '', value: '' });
};

// Remove custom outbound parameter row
const removeOutboundParam = index => {
  outboundParams.value.splice(index, 1);
  if (outboundParams.value.length === 0) {
    outboundParams.value.push({ key: '', value: '' });
  }
};

// Save custom outbound params to additional_attributes and re-sync webhook
const saveOutboundParams = async () => {
  try {
    savingOutboundParams.value = true;
    const currentAttrs = snakecaseKeys(
      props.inbox.channel?.additionalAttributes || {},
      { deep: true }
    );
    const paramsObj = {};
    outboundParams.value.forEach(p => {
      if (p.key && p.key.trim()) paramsObj[p.key.trim()] = p.value;
    });
    await store.dispatch('inboxes/updateInbox', {
      id: props.inbox.id,
      formData: false,
      channel: {
        additional_attributes: {
          ...currentAttrs,
          outbound_params: paramsObj,
        },
      },
    });
    useAlert('Custom parameters saved!');
    // Re-sync outbound webhook to include params in URL
    await syncOutboundWebhook();
  } catch (error) {
    useAlert(
      `Error saving parameters: ${error.response?.data?.error || error.message}`
    );
  } finally {
    savingOutboundParams.value = false;
  }
};

// Toggle tri-state for new webhook
const toggleNewWebhookTriState = field => {
  const current = newWebhook.value[field];
  if (current === null) {
    newWebhook.value[field] = false;
  } else if (current === false) {
    newWebhook.value[field] = true;
  } else {
    newWebhook.value[field] = null;
  }
};

const loadOutboundParams = () => {
  const stored =
    props.inbox.channel?.additionalAttributes?.outboundParams || {};
  const entries = Object.entries(stored);
  outboundParams.value =
    entries.length > 0
      ? entries.map(([key, value]) => ({ key, value: String(value) }))
      : [{ key: '', value: '' }];
};

watch(
  () => props.inbox.channel?.additionalAttributes?.outboundParams,
  () => loadOutboundParams(),
  { deep: true }
);

onMounted(() => {
  loadOutboundParams();
  checkQuepasaConfig();
  loadWebhooks();
  validateWebhookConfig();
});
</script>

<template>
  <div class="mx-8 py-8 flex flex-col gap-6">
    <!-- Super Admin Configuration Alert -->
    <!-- Header -->
    <div class="flex items-center justify-between">
      <div>
        <h3 class="text-base text-n-brand mb-0 font-medium">
          {{ $t('QUEPASA_INBOX.WEBHOOKS.TITLE') }}
        </h3>
        <p class="text-sm mb-2 text-n-slate-11 leading-5 tracking-normal mt-2">
          {{ $t('QUEPASA_INBOX.WEBHOOKS.DESCRIPTION') }}
        </p>
      </div>
      <div v-if="isSuperAdmin" class="flex items-center gap-2">
        <button
          class="flex items-center gap-2 px-4 py-2 text-sm font-medium text-woot-600 bg-white border border-woot-500 hover:bg-woot-50 rounded-lg transition-colors"
          @click="showNewWebhookModal = true"
        >
          <fluent-icon icon="add" size="16" class="flex-shrink-0" />
          {{ $t('QUEPASA_INBOX.WEBHOOKS.NEW_WEBHOOK') }}
        </button>
        <button
          class="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-woot-500 hover:bg-woot-600 rounded-lg transition-colors"
          @click="loadWebhooks"
        >
          <fluent-icon icon="arrow-clockwise" size="16" class="flex-shrink-0" />
          {{ $t('QUEPASA_INBOX.WEBHOOKS.REFRESH') }}
        </button>
      </div>
    </div>

    <!-- Custom Outbound Webhook Parameters -->
    <div class="border border-slate-200 dark:border-slate-700 rounded-lg p-4">
      <div class="flex items-center justify-between mb-2">
        <h4 class="text-sm font-medium text-slate-900 dark:text-slate-100">
          <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
          Custom Outbound Parameters
        </h4>
      </div>
      <p class="text-xs text-slate-600 dark:text-slate-400 mb-3">
        <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
        Extra query parameters added to the outbound webhook URL (Chatwoot
        &rarr; N8N). E.g. cwhost, custom flags.
      </p>

      <div class="flex flex-col gap-2">
        <div
          v-for="(param, idx) in outboundParams"
          :key="idx"
          class="flex items-center gap-2"
        >
          <input
            v-model="param.key"
            type="text"
            placeholder="key"
            class="flex-1 px-3 py-2 text-xs border border-slate-200 dark:border-slate-600 rounded focus:ring-2 focus:ring-woot-500 font-mono bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
          />
          <input
            v-model="param.value"
            type="text"
            placeholder="value"
            class="flex-1 px-3 py-2 text-xs border border-slate-200 dark:border-slate-600 rounded focus:ring-2 focus:ring-woot-500 font-mono bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
          />
          <button
            type="button"
            class="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors flex items-center justify-center"
            :disabled="outboundParams.length === 1 && !param.key"
            @click="removeOutboundParam(idx)"
          >
            <fluent-icon icon="delete" size="16" />
          </button>
        </div>
      </div>

      <div class="flex items-center gap-2 mt-3">
        <button
          type="button"
          class="flex items-center gap-2 px-3 py-2 text-xs text-woot-500 hover:bg-woot-50 dark:hover:bg-woot-900/20 rounded transition-colors"
          @click="addOutboundParam"
        >
          <fluent-icon icon="add" size="16" />
          <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
          Add Parameter
        </button>
        <button
          class="flex items-center gap-2 px-4 py-2 text-xs font-medium text-white bg-woot-500 hover:bg-woot-600 rounded transition-colors disabled:opacity-50"
          :disabled="savingOutboundParams || !outboundParamsDirty"
          @click="saveOutboundParams"
        >
          <Spinner v-if="savingOutboundParams" size="tiny" />
          <fluent-icon v-else icon="save" size="16" class="flex-shrink-0" />
          <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
          Save &amp; Sync Webhook
        </button>
      </div>
    </div>

    <!-- Webhook Validation Status -->
    <div class="border border-slate-200 dark:border-slate-700 rounded-lg p-4">
      <h4 class="text-sm font-medium text-slate-900 dark:text-slate-100 mb-3">
        Webhook Configuration Status
      </h4>

      <!-- Loading state -->
      <div
        v-if="loadingValidation"
        class="flex items-center justify-center py-4"
      >
        <Spinner size="small" />
      </div>

      <div v-else>
        <!-- Outgoing Messages (Chatwoot → N8N → QuePasa) - Always visible (Chatwoot DB) -->
        <div
          class="flex items-start gap-3 mb-3 pb-3 border-b border-slate-200 dark:border-slate-700"
        >
          <div class="flex-shrink-0 mt-1">
            <fluent-icon
              :icon="
                webhookValidation?.from_chatwoot?.valid
                  ? 'checkmark-circle'
                  : 'warning'
              "
              :class="
                webhookValidation?.from_chatwoot?.valid
                  ? 'text-green-600'
                  : 'text-yellow-600'
              "
              size="20"
            />
          </div>
          <div class="flex-1">
            <p class="text-sm font-medium text-slate-900 dark:text-slate-100">
              Outgoing Messages (Chatwoot → N8N → QuePasa)
            </p>
            <p class="text-xs text-slate-600 dark:text-slate-400 mt-1">
              Stored in Chatwoot database (channel.webhook_url with params)
            </p>
            <div
              v-if="
                webhookValidation && !webhookValidation.from_chatwoot?.valid
              "
              class="mt-2"
            >
              <p
                :class="
                  isMinorWarning(webhookValidation.from_chatwoot?.error_type)
                    ? 'text-xs text-amber-600 dark:text-amber-400'
                    : 'text-xs text-yellow-700 dark:text-yellow-400'
                "
              >
                <span class="font-medium">
                  {{
                    isMinorWarning(webhookValidation.from_chatwoot?.error_type)
                      ? 'ℹ️'
                      : '⚠'
                  }}
                  {{
                    webhookValidation.from_chatwoot?.message ||
                    'Invalid configuration'
                  }}
                </span>
              </p>
              <p
                v-if="webhookValidation.from_chatwoot?.error_type"
                class="text-xs text-slate-600 dark:text-slate-400 mt-1"
              >
                {{
                  isMinorWarning(webhookValidation.from_chatwoot?.error_type)
                    ? 'Note'
                    : 'Error'
                }}:
                {{
                  getErrorTypeLabel(webhookValidation.from_chatwoot.error_type)
                }}
              </p>
              <button
                v-if="canFixWebhooks"
                class="mt-2 flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-white bg-woot-500 hover:bg-woot-600 rounded transition-colors"
                :disabled="syncingFromWebhook"
                @click="syncOutboundWebhook"
              >
                <fluent-icon
                  icon="arrow-clockwise"
                  size="14"
                  class="flex-shrink-0"
                />
                {{ syncingFromWebhook ? 'Syncing...' : 'Fix Webhook' }}
              </button>
            </div>
          </div>
        </div>

        <!-- Incoming Messages (QuePasa → N8N → Chatwoot) - Only if server initialized (QuePasa) -->
        <div
          v-if="serverInfo && !serverInfoError"
          class="flex items-start gap-3"
        >
          <div class="flex-shrink-0 mt-1">
            <fluent-icon
              :icon="
                webhookValidation.to_chatwoot?.valid
                  ? 'checkmark-circle'
                  : 'warning'
              "
              :class="
                webhookValidation.to_chatwoot?.valid
                  ? 'text-green-600'
                  : 'text-yellow-600'
              "
              size="20"
            />
          </div>
          <div class="flex-1">
            <p class="text-sm font-medium text-slate-900 dark:text-slate-100">
              Incoming Messages (QuePasa → N8N → Chatwoot)
            </p>
            <p class="text-xs text-slate-600 dark:text-slate-400 mt-1">
              Configured in QuePasa dispatching (base URL + extras in POST body)
            </p>
            <div v-if="!webhookValidation.to_chatwoot?.valid" class="mt-2">
              <p
                :class="
                  isMinorWarning(webhookValidation.to_chatwoot?.error_type)
                    ? 'text-xs text-amber-600 dark:text-amber-400'
                    : 'text-xs text-yellow-700 dark:text-yellow-400'
                "
              >
                <span class="font-medium">
                  {{
                    isMinorWarning(webhookValidation.to_chatwoot?.error_type)
                      ? 'ℹ️'
                      : '⚠'
                  }}
                  {{
                    webhookValidation.to_chatwoot?.message ||
                    'Invalid configuration'
                  }}
                </span>
              </p>
              <p
                v-if="webhookValidation.to_chatwoot?.error_type"
                class="text-xs text-slate-600 dark:text-slate-400 mt-1"
              >
                {{
                  isMinorWarning(webhookValidation.to_chatwoot?.error_type)
                    ? 'Note'
                    : 'Error'
                }}:
                {{
                  getErrorTypeLabel(webhookValidation.to_chatwoot.error_type)
                }}
              </p>
              <button
                v-if="canFixWebhooks"
                class="mt-2 flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-white bg-woot-500 hover:bg-woot-600 rounded transition-colors"
                :disabled="syncingToWebhook"
                @click="syncInboundWebhook"
              >
                <fluent-icon
                  icon="arrow-clockwise"
                  size="14"
                  class="flex-shrink-0"
                />
                {{ syncingToWebhook ? 'Syncing...' : 'Fix Webhook' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loadingWebhooks" class="flex items-center justify-center py-12">
      <Spinner size="large" />
    </div>

    <!-- Server Not Initialized State -->
    <div
      v-else-if="serverInfoError === 'server_not_found'"
      class="p-6 border border-blue-200 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-800 rounded-lg"
    >
      <div class="flex items-start gap-3">
        <fluent-icon
          icon="info"
          size="20"
          class="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5"
        />
        <div>
          <p class="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">
            {{ $t('QUEPASA_INBOX.WEBHOOKS.SERVER_NOT_INITIALIZED_TITLE') }}
          </p>
          <p class="text-sm text-blue-800 dark:text-blue-200">
            {{ $t('QUEPASA_INBOX.WEBHOOKS.SERVER_NOT_INITIALIZED_DESC') }}
          </p>
        </div>
      </div>
    </div>

    <!-- Error State -->
    <div
      v-else-if="serverInfoError"
      class="p-4 border border-yellow-200 bg-yellow-50 dark:bg-yellow-900/20 dark:border-yellow-800 rounded-lg"
    >
      <p class="text-sm text-yellow-800 dark:text-yellow-200">
        {{ `${$t('QUEPASA_INBOX.WEBHOOKS.ERROR')}: ${serverInfoError}` }}
      </p>
    </div>

    <!-- Empty State -->
    <div
      v-else-if="webhooks.length === 0"
      class="flex flex-col items-center justify-center py-12 border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-lg"
    >
      <fluent-icon
        icon="globe"
        size="48"
        class="text-slate-400 dark:text-slate-500 mb-4"
      />
      <p class="text-sm font-medium text-slate-600 dark:text-slate-400">
        {{ $t('QUEPASA_INBOX.WEBHOOKS.EMPTY') }}
      </p>
    </div>

    <!-- Webhooks List -->
    <div v-else class="flex flex-col gap-3">
      <AccordionItem
        v-for="(webhook, index) in webhooks"
        :key="index"
        :title="webhook.connection_string + (webhook.failure ? ' ⚠️' : '')"
        :is-open="expandedWebhooks[index]"
        @toggle="toggleWebhook(index)"
      >
        <template #button>
          <button
            v-if="isSuperAdmin"
            class="p-2 mr-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
            @click.stop="deleteWebhook(index)"
          >
            <fluent-icon icon="delete" size="18" />
          </button>
        </template>

        <div class="flex flex-col gap-4 p-4">
          <!-- Action Buttons -->
          <div
            class="flex items-center gap-2 pb-3 border-b border-slate-200 dark:border-slate-700"
          >
            <button
              class="flex items-center gap-2 px-3 py-2 text-xs font-medium text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
              @click="testWebhook(index)"
            >
              <fluent-icon icon="send" size="16" class="flex-shrink-0" />
              {{ $t('QUEPASA_INBOX.WEBHOOKS.TEST') }}
            </button>
            <button
              class="flex items-center gap-2 px-3 py-2 text-xs font-medium text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded transition-colors"
              @click="validateWebhook(index)"
            >
              <fluent-icon icon="checkmark" size="16" class="flex-shrink-0" />
              {{ $t('QUEPASA_INBOX.WEBHOOKS.VALIDATE') }}
            </button>
            <button
              class="flex items-center gap-2 px-3 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 rounded transition-colors"
              @click="copyWebhookUrl(webhook.connection_string)"
            >
              <fluent-icon icon="copy" size="16" class="flex-shrink-0" />
              {{ $t('QUEPASA_INBOX.WEBHOOKS.COPY_URL') }}
            </button>
          </div>

          <!-- Failure Warning -->
          <div
            v-if="webhook.failure"
            class="p-3 border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 rounded-lg"
          >
            <div class="flex items-start gap-2">
              <fluent-icon
                icon="warning"
                size="20"
                class="text-amber-600 dark:text-amber-500 flex-shrink-0 mt-0.5"
              />
              <div class="flex-1">
                <p
                  class="text-sm font-medium text-amber-800 dark:text-amber-200"
                >
                  {{ $t('QUEPASA_INBOX.WEBHOOKS.FAILURE_DETECTED') }}
                </p>
                <p class="text-xs text-amber-700 dark:text-amber-300 mt-1">
                  {{ webhook.failure }}
                </p>
              </div>
            </div>
          </div>

          <!-- Track ID (editable for super admin) -->
          <div
            v-if="isSuperAdmin || webhook.trackid"
            class="flex flex-col gap-1"
          >
            <span
              class="text-xs font-medium text-slate-600 dark:text-slate-400"
            >
              {{ $t('QUEPASA_INBOX.WEBHOOKS.TRACK_ID') }}
            </span>
            <input
              v-if="isSuperAdmin"
              v-model="editingWebhooks[index].trackid"
              type="text"
              class="px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded focus:ring-2 focus:ring-woot-500 font-mono"
              :placeholder="$t('QUEPASA_INBOX.WEBHOOKS.TRACK_ID_PLACEHOLDER')"
            />
            <code
              v-else
              class="px-3 py-2 text-xs bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded font-mono"
            >
              {{ webhook.trackid }}
            </code>
          </div>

          <!-- Boolean Options -->
          <div
            v-if="isSuperAdmin || hasAnyBooleanOption(webhook)"
            class="flex flex-col gap-3"
          >
            <span
              class="text-xs font-medium text-slate-600 dark:text-slate-400"
            >
              {{ $t('QUEPASA_INBOX.WEBHOOKS.OPTIONS') }}
            </span>
            <div class="grid grid-cols-2 gap-3">
              <label
                v-if="isSuperAdmin || webhook.groups !== undefined"
                class="flex items-center gap-3"
              >
                <button
                  v-if="isSuperAdmin"
                  type="button"
                  class="w-10 h-10 flex items-center justify-center border-2 rounded-lg transition-all hover:scale-105 cursor-pointer"
                  :class="{
                    'bg-green-50 border-green-600 hover:bg-green-100':
                      editingWebhooks[index].groups === true,
                    'bg-red-50 border-red-600 hover:bg-red-100':
                      editingWebhooks[index].groups === false,
                    'bg-slate-50 border-slate-300 hover:bg-slate-100':
                      editingWebhooks[index].groups === null,
                  }"
                  @click="toggleTriState(index, 'groups')"
                >
                  <fluent-icon
                    :icon="
                      getTriStateDisplay(editingWebhooks[index].groups).icon
                    "
                    size="20"
                    :class="
                      getTriStateDisplay(editingWebhooks[index].groups).color
                    "
                  />
                </button>
                <span v-else class="w-10 h-10 flex items-center justify-center">
                  <fluent-icon
                    :icon="getTriStateDisplay(webhook.groups).icon"
                    size="20"
                    :class="getTriStateDisplay(webhook.groups).color"
                  />
                </span>
                <span
                  class="text-sm font-medium text-slate-700 dark:text-slate-300"
                >
                  {{ $t('QUEPASA_INBOX.WEBHOOKS.GROUPS') }}
                </span>
              </label>

              <label
                v-if="isSuperAdmin || webhook.broadcasts !== undefined"
                class="flex items-center gap-3"
              >
                <button
                  v-if="isSuperAdmin"
                  type="button"
                  class="w-10 h-10 flex items-center justify-center border-2 rounded-lg transition-all hover:scale-105 cursor-pointer"
                  :class="{
                    'bg-green-50 border-green-600 hover:bg-green-100':
                      editingWebhooks[index].broadcasts === true,
                    'bg-red-50 border-red-600 hover:bg-red-100':
                      editingWebhooks[index].broadcasts === false,
                    'bg-slate-50 border-slate-300 hover:bg-slate-100':
                      editingWebhooks[index].broadcasts === null,
                  }"
                  @click="toggleTriState(index, 'broadcasts')"
                >
                  <fluent-icon
                    :icon="
                      getTriStateDisplay(editingWebhooks[index].broadcasts).icon
                    "
                    size="20"
                    :class="
                      getTriStateDisplay(editingWebhooks[index].broadcasts)
                        .color
                    "
                  />
                </button>
                <span v-else class="w-10 h-10 flex items-center justify-center">
                  <fluent-icon
                    :icon="getTriStateDisplay(webhook.broadcasts).icon"
                    size="20"
                    :class="getTriStateDisplay(webhook.broadcasts).color"
                  />
                </span>
                <span
                  class="text-sm font-medium text-slate-700 dark:text-slate-300"
                >
                  {{ $t('QUEPASA_INBOX.WEBHOOKS.BROADCASTS') }}
                </span>
              </label>

              <label
                v-if="isSuperAdmin || webhook.calls !== undefined"
                class="flex items-center gap-3"
              >
                <button
                  v-if="isSuperAdmin"
                  type="button"
                  class="w-10 h-10 flex items-center justify-center border-2 rounded-lg transition-all hover:scale-105 cursor-pointer"
                  :class="{
                    'bg-green-50 border-green-600 hover:bg-green-100':
                      editingWebhooks[index].calls === true,
                    'bg-red-50 border-red-600 hover:bg-red-100':
                      editingWebhooks[index].calls === false,
                    'bg-slate-50 border-slate-300 hover:bg-slate-100':
                      editingWebhooks[index].calls === null,
                  }"
                  @click="toggleTriState(index, 'calls')"
                >
                  <fluent-icon
                    :icon="
                      getTriStateDisplay(editingWebhooks[index].calls).icon
                    "
                    size="20"
                    :class="
                      getTriStateDisplay(editingWebhooks[index].calls).color
                    "
                  />
                </button>
                <span v-else class="w-10 h-10 flex items-center justify-center">
                  <fluent-icon
                    :icon="getTriStateDisplay(webhook.calls).icon"
                    size="20"
                    :class="getTriStateDisplay(webhook.calls).color"
                  />
                </span>
                <span
                  class="text-sm font-medium text-slate-700 dark:text-slate-300"
                >
                  {{ $t('QUEPASA_INBOX.WEBHOOKS.CALLS') }}
                </span>
              </label>

              <label
                v-if="isSuperAdmin || webhook.readreceipts !== undefined"
                class="flex items-center gap-3"
              >
                <button
                  v-if="isSuperAdmin"
                  type="button"
                  class="w-10 h-10 flex items-center justify-center border-2 rounded-lg transition-all hover:scale-105 cursor-pointer"
                  :class="{
                    'bg-green-50 border-green-600 hover:bg-green-100':
                      editingWebhooks[index].readreceipts === true,
                    'bg-red-50 border-red-600 hover:bg-red-100':
                      editingWebhooks[index].readreceipts === false,
                    'bg-slate-50 border-slate-300 hover:bg-slate-100':
                      editingWebhooks[index].readreceipts === null,
                  }"
                  @click="toggleTriState(index, 'readreceipts')"
                >
                  <fluent-icon
                    :icon="
                      getTriStateDisplay(editingWebhooks[index].readreceipts)
                        .icon
                    "
                    size="20"
                    :class="
                      getTriStateDisplay(editingWebhooks[index].readreceipts)
                        .color
                    "
                  />
                </button>
                <span v-else class="w-10 h-10 flex items-center justify-center">
                  <fluent-icon
                    :icon="getTriStateDisplay(webhook.readreceipts).icon"
                    size="20"
                    :class="getTriStateDisplay(webhook.readreceipts).color"
                  />
                </span>
                <span
                  class="text-sm font-medium text-slate-700 dark:text-slate-300"
                >
                  {{ $t('QUEPASA_INBOX.WEBHOOKS.READ_RECEIPTS') }}
                </span>
              </label>

              <label
                v-if="isSuperAdmin || webhook.forwardinternal !== undefined"
                class="flex items-center gap-2"
              >
                <input
                  v-if="isSuperAdmin"
                  v-model="editingWebhooks[index].forwardinternal"
                  type="checkbox"
                  class="w-4 h-4 text-woot-500 border-slate-300 rounded focus:ring-woot-500"
                />
                <span v-else class="w-4 h-4 flex items-center justify-center">
                  <fluent-icon
                    :icon="webhook.forwardinternal ? 'checkmark' : 'dismiss'"
                    size="16"
                    :class="
                      webhook.forwardinternal
                        ? 'text-green-600'
                        : 'text-slate-400'
                    "
                  />
                </span>
                <span class="text-sm text-slate-700 dark:text-slate-300">
                  {{ $t('QUEPASA_INBOX.WEBHOOKS.FORWARD_INTERNAL') }}
                </span>
              </label>
            </div>
          </div>

          <!-- Extra Parameters -->
          <div class="flex flex-col gap-3">
            <div class="flex items-center justify-between">
              <span
                class="text-xs font-medium text-slate-600 dark:text-slate-400"
              >
                {{ $t('QUEPASA_INBOX.WEBHOOKS.EXTRAS') }}
              </span>
            </div>

            <!-- Editable extras for super admin -->
            <div v-if="isSuperAdmin" class="flex flex-col gap-2">
              <div
                v-for="(extra, extraIdx) in editingWebhooks[index].extras"
                :key="extraIdx"
                class="flex items-center gap-2"
              >
                <input
                  v-model="extra.key"
                  type="text"
                  :placeholder="$t('QUEPASA_INBOX.WEBHOOKS.KEY_PLACEHOLDER')"
                  class="flex-1 px-3 py-2 text-xs border border-slate-200 dark:border-slate-600 rounded focus:ring-2 focus:ring-woot-500 font-mono"
                />
                <input
                  v-model="extra.value"
                  type="text"
                  :placeholder="$t('QUEPASA_INBOX.WEBHOOKS.VALUE_PLACEHOLDER')"
                  class="flex-1 px-3 py-2 text-xs border border-slate-200 dark:border-slate-600 rounded focus:ring-2 focus:ring-woot-500 font-mono"
                />
                <button
                  type="button"
                  class="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors flex items-center justify-center"
                  :disabled="editingWebhooks[index].extras.length === 1"
                  @click="removeExtra(index, extraIdx)"
                >
                  <fluent-icon icon="delete" size="16" />
                </button>
              </div>

              <!-- Action Buttons -->
              <div class="flex items-center gap-2 mt-2">
                <button
                  type="button"
                  class="flex items-center gap-2 px-3 py-2 text-xs text-woot-500 hover:bg-woot-50 dark:hover:bg-woot-900/20 rounded transition-colors"
                  @click="addExtra(index)"
                >
                  <fluent-icon icon="add" size="16" />
                  {{ $t('QUEPASA_INBOX.WEBHOOKS.ADD_EXTRA') }}
                </button>
                <NextButton
                  size="sm"
                  :label="$t('QUEPASA_INBOX.WEBHOOKS.SAVE')"
                  :is-loading="savingWebhook[index]"
                  @click="saveWebhook(index)"
                />
              </div>
            </div>

            <!-- Read-only extras for normal users -->
            <div v-else-if="webhook.extra" class="flex flex-wrap gap-2">
              <span
                v-for="(value, key) in webhook.extra"
                :key="key"
                class="px-3 py-1 text-xs bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded font-mono"
              >
                <span class="font-semibold text-slate-700 dark:text-slate-300">
                  {{ `${key}:` }}
                </span>
                <span class="text-slate-600 dark:text-slate-400">
                  {{ value }}
                </span>
              </span>
            </div>
            <p v-else class="text-xs text-slate-500 dark:text-slate-500 italic">
              {{ $t('QUEPASA_INBOX.WEBHOOKS.NO_EXTRAS') }}
            </p>
          </div>
        </div>
      </AccordionItem>
    </div>

    <!-- New Webhook Modal -->
    <div
      v-if="showNewWebhookModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      @click.self="showNewWebhookModal = false"
    >
      <div
        class="bg-white dark:bg-slate-900 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
      >
        <div
          class="sticky top-0 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 px-6 py-4 flex items-center justify-between"
        >
          <h4 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {{ $t('QUEPASA_INBOX.WEBHOOKS.NEW_WEBHOOK') }}
          </h4>
          <button
            class="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
            @click="showNewWebhookModal = false"
          >
            <fluent-icon icon="dismiss" size="20" />
          </button>
        </div>

        <div class="p-6 flex flex-col gap-4">
          <!-- URL -->
          <div class="flex flex-col gap-2">
            <label
              class="text-sm font-medium text-slate-700 dark:text-slate-300"
            >
              {{ $t('QUEPASA_INBOX.WEBHOOKS.URL') }} *
            </label>
            <input
              v-model="newWebhook.url"
              type="text"
              placeholder="http://example.com/webhook"
              class="px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-woot-500 dark:bg-slate-800"
            />
          </div>

          <!-- Track ID -->
          <div class="flex flex-col gap-2">
            <label
              class="text-sm font-medium text-slate-700 dark:text-slate-300"
            >
              {{ $t('QUEPASA_INBOX.WEBHOOKS.TRACK_ID') }} *
            </label>
            <input
              v-model="newWebhook.trackid"
              type="text"
              placeholder="my-webhook"
              class="px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-woot-500 dark:bg-slate-800"
            />
          </div>

          <!-- Forward Internal -->
          <div class="flex items-center gap-2">
            <input
              v-model="newWebhook.forwardinternal"
              type="checkbox"
              class="w-4 h-4 text-woot-500 border-slate-300 rounded focus:ring-woot-500"
            />
            <label class="text-sm text-slate-700 dark:text-slate-300">
              {{ $t('QUEPASA_INBOX.WEBHOOKS.FORWARD_INTERNAL') }}
            </label>
          </div>

          <!-- Boolean Options -->
          <div class="flex flex-col gap-3">
            <label
              class="text-sm font-medium text-slate-700 dark:text-slate-300"
            >
              {{ $t('QUEPASA_INBOX.WEBHOOKS.OPTIONS') }}
            </label>
            <div class="grid grid-cols-2 gap-3">
              <button
                v-for="option in [
                  'groups',
                  'broadcasts',
                  'calls',
                  'readreceipts',
                ]"
                :key="option"
                type="button"
                class="flex items-center gap-3 p-3 border-2 rounded-lg transition-all hover:scale-105 cursor-pointer"
                :class="{
                  'bg-green-50 border-green-600': newWebhook[option] === true,
                  'bg-red-50 border-red-600': newWebhook[option] === false,
                  'bg-slate-50 border-slate-300': newWebhook[option] === null,
                }"
                @click="toggleNewWebhookTriState(option)"
              >
                <div class="w-8 h-8 flex items-center justify-center">
                  <fluent-icon
                    :icon="getTriStateDisplay(newWebhook[option]).icon"
                    size="20"
                    :class="getTriStateDisplay(newWebhook[option]).color"
                  />
                </div>
                <span class="text-sm font-medium text-slate-700">
                  {{ $t(`QUEPASA_INBOX.WEBHOOKS.${option.toUpperCase()}`) }}
                </span>
              </button>
            </div>
          </div>

          <!-- Extras -->
          <div class="flex flex-col gap-3">
            <label
              class="text-sm font-medium text-slate-700 dark:text-slate-300"
            >
              {{ $t('QUEPASA_INBOX.WEBHOOKS.EXTRAS') }}
            </label>
            <div
              v-for="(extra, idx) in newWebhook.extras"
              :key="idx"
              class="flex items-center gap-2"
            >
              <input
                v-model="extra.key"
                type="text"
                :placeholder="$t('QUEPASA_INBOX.WEBHOOKS.KEY_PLACEHOLDER')"
                class="flex-1 px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-woot-500 font-mono dark:bg-slate-800"
              />
              <input
                v-model="extra.value"
                type="text"
                :placeholder="$t('QUEPASA_INBOX.WEBHOOKS.VALUE_PLACEHOLDER')"
                class="flex-1 px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-woot-500 font-mono dark:bg-slate-800"
              />
              <button
                type="button"
                class="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                :disabled="newWebhook.extras.length === 1"
                @click="removeNewWebhookExtra(idx)"
              >
                <fluent-icon icon="delete" size="16" />
              </button>
            </div>
            <button
              type="button"
              class="flex items-center gap-2 px-3 py-2 text-sm text-woot-600 hover:bg-woot-50 rounded-lg transition-colors self-start"
              @click="addNewWebhookExtra"
            >
              <fluent-icon icon="add" size="16" />
              {{ $t('QUEPASA_INBOX.WEBHOOKS.ADD_EXTRA') }}
            </button>
          </div>
        </div>

        <div
          class="sticky bottom-0 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700 px-6 py-4 flex items-center justify-end gap-3"
        >
          <NextButton
            color-scheme="secondary"
            @click="showNewWebhookModal = false"
          >
            {{ $t('QUEPASA_INBOX.WEBHOOKS.CANCEL') }}
          </NextButton>
          <NextButton :is-loading="creatingWebhook" @click="createWebhook">
            {{ $t('QUEPASA_INBOX.WEBHOOKS.CREATE') }}
          </NextButton>
        </div>
      </div>
    </div>
  </div>
</template>
